import * as i0 from '@angular/core';
import { Injectable, Inject } from '@angular/core';
import { BehaviorSubject, mergeMap, tap, map } from 'rxjs';
import { ServiceHandle } from '@tangilla/tg-ng-models/src/lib/types';
import jwtDecode from 'jwt-decode';
import { isJwtValid, RouterMap } from '@tangilla/tg-ng-core/src/lib/utils';
import { CoreServicesModule } from '@tangilla/tg-ng-core/src/lib/core-services';
import * as i1 from '@tangilla/tg-ng-core/src/lib/services';
import * as i2 from 'ngx-localstorage';
import * as i3 from '@angular/router';

class AuthService {
    constructor(sessionApiService, userApiService, localStorageService, router, env, projectConfig) {
        this.sessionApiService = sessionApiService;
        this.userApiService = userApiService;
        this.localStorageService = localStorageService;
        this.router = router;
        this.env = env;
        this.projectConfig = projectConfig;
        // @ts-ignore // Need this to avoid BehavoirSubject(null) error
        this.me$ = new BehaviorSubject(null);
        this.sessionData = this.getSessionFromStorage();
    }
    get associationSmallLogo() {
        return `${this.env.api.assets}/associations/${this.associationNrdsId}/img/android-icon-72x72.png`;
    }
    get associationNrdsId() {
        return this.currentUserEntity?.association_nrds_id;
    }
    get officeNrdsId() {
        return '060300298';
    }
    get userPersonId() {
        return this.currentUserEntity?.person_id;
    }
    get memberNrdsId() {
        return this.currentUserEntity?.member_nrds_id;
    }
    get currentUserEntity() {
        return this.tokenData && (jwtDecode(this.tokenData).person || jwtDecode(this.tokenData).billing_entity_user);
    }
    get idToken() {
        return this.tokenData && jwtDecode(this.tokenData).id_token;
    }
    get accountToken() {
        return this.sessionData?.services?.[ServiceHandle.ACCOUNT_SERVICE].token;
    }
    get memberToken() {
        return this.sessionData?.services?.[ServiceHandle.MEMBER_SERVICE].token;
    }
    get committeeToken() {
        return this.sessionData?.services?.[ServiceHandle.COMMITTEE_SERVICE].token;
    }
    get communicationToken() {
        return this.sessionData?.services?.[ServiceHandle.COMMUNICATION_SERVICE].token;
    }
    get eventToken() {
        return this.sessionData?.services?.[ServiceHandle.EVENT_SERVICE].token;
    }
    get billingToken() {
        return this.sessionData?.services?.[ServiceHandle.BILLING_SERVICE].token;
    }
    get utilitiesToken() {
        return this.sessionData?.services?.[ServiceHandle.UTILITIES_SERVICE].token;
    }
    get emailToken() {
        return this.sessionData?.services?.[ServiceHandle.EMAIL_SERVICE].token;
    }
    get isStateAssociation() {
        return this.tokenData && jwtDecode(this.tokenData).is_state_association;
    }
    get loggedInAs() {
        return this.tokenData && jwtDecode(this.tokenData).logged_in_as;
    }
    get staffUserId() {
        return this.tokenData && jwtDecode(this.tokenData).staff_user_id;
    }
    get govtToken() {
        return this.sessionData?.services?.[ServiceHandle.GOVT_AFFAIRS_SERVICE].token;
    }
    get formsToken() {
        return this.sessionData?.services?.[ServiceHandle.FORMS_SERVICE].token;
    }
    get reportingToken() {
        return this.sessionData?.services?.[ServiceHandle.REPORTING_SERVICE].token;
    }
    get beanCounterToken() {
        return this.sessionData?.services?.[ServiceHandle.BEAN_COUNTER_SERVICE].token;
    }
    get stateEducationToken() {
        return this.sessionData?.services?.[ServiceHandle.STATE_EDUCATION_SERVICE].token;
    }
    get isAuthenticated() {
        return !!this.accountToken && isJwtValid(this.accountToken);
    }
    get isExpired() {
        return this.accountToken && !isJwtValid(this.accountToken);
    }
    get userId() {
        return this.sessionData?.user_id || '';
    }
    get activeServicesSessionData() {
        return (this.sessionData && Object.values(this.sessionData.services)) || null;
    }
    updateServicesSessionData(services) {
        const update = {};
        services.forEach(service => {
            // @ts-ignore
            update[service.programming_handle] = service;
        });
        if (this.sessionData) {
            this.sessionData.services = update;
            this.storeSession(this.sessionData);
        }
    }
    get timeZoneSessionData() {
        return this.sessionData && this.sessionData.time_zone;
    }
    updateTimeZone(timezone) {
        if (this.sessionData) {
            this.sessionData.time_zone = timezone;
            this.storeSession(this.sessionData);
        }
    }
    tokenLogin(token) {
        return this.sessionApiService.create({ token })
            .pipe(mergeMap(async (session) => {
            this.sessionData = session;
            this.storeTokenData(token);
            this.storeSession(session);
            this.updateTimeZone(session.services_timezone);
            return true;
        }));
    }
    storeSession(session) {
        this.localStorageService.set(this.env.localStorage.session, session);
    }
    storeTokenData(token) {
        this.localStorageService.set(this.env.localStorage.person, token);
    }
    get tokenData() {
        return this.localStorageService.get(this.env.localStorage.person);
    }
    getSessionFromStorage() {
        return this.localStorageService.get(this.env.localStorage.session);
    }
    getCurrentUserInfo() {
        return this.userApiService.getOne(this.userId).pipe(tap((user) => {
            this.me = user;
            this.me$.next(user);
        }));
    }
    idpLogout() {
        const associationNrdsId = this.associationNrdsId;
        if (this.localStorageService.get(this.env.localStorage.session) || this.localStorageService.get(this.env.localStorage.person)) {
            const idToken = this.idToken;
            this.clearSession();
            window.location.href = `${this.env.api.account}/auth/${this.projectConfig.applicationName}/openid-connect/${associationNrdsId}/logout${idToken ? `?id_token=${idToken}` : ''}`;
        }
        else {
            this.router.navigate([RouterMap.getProjectFirstPage(this.projectConfig?.firstPage)]);
        }
    }
    login(email, password) {
        const body = { email, password };
        return this.sessionApiService.create(body)
            .pipe(tap(session => {
            this.sessionData = session;
            this.storeSession(session);
        }), map(() => true));
    }
    /*  tokenLogin(token: any): Observable<boolean> {
        return this.sessionApiService.create({token})
          .pipe(mergeMap(async session => {
              this.sessionData = session;
              await this.oauthAvatarService.setSessionPictureFromJwt(token, this.sessionData);
              this.storeSession(session);
              return true;
            }),
          );
      }*/
    logout() {
        if (this.projectConfig.auth.authenticationType === 'SSO') {
            this.idpLogout();
        }
        else {
            this.clearSession();
            this.router.navigate([`/${RouterMap.Unauthorized.ROOT}`]);
        }
    }
    clearSession() {
        this.sessionData = undefined;
        this.localStorageService.remove(this.env.localStorage.session);
        this.localStorageService.remove(this.env.localStorage.person);
    }
    redirectToMaintenancePage(queryParams) {
        this.router.navigate([RouterMap.Auth.MAINTENANCE], queryParams ? { queryParams } : undefined);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthService, deps: [{ token: i1.SessionApiService }, { token: i1.UserApiService }, { token: i2.LocalStorageService }, { token: i3.Router }, { token: 'env' }, { token: 'projectConfig' }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthService, providedIn: CoreServicesModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: CoreServicesModule
                }]
        }], ctorParameters: function () { return [{ type: i1.SessionApiService }, { type: i1.UserApiService }, { type: i2.LocalStorageService }, { type: i3.Router }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['env']
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['projectConfig']
                }] }]; } });

/**
 * Generated bundle index. Do not edit.
 */

export { AuthService };
